<template>
    <div>
        <h5>{{ selectedType }}</h5>
        <!-- Form Body -->
        <b-form>
            <!-- Form Type Select (only for new forms) -->
            <b-form-group v-if="!admissionformId" label-for="formType">
                <label for="formType" class="font-weight-bold">
                    Form Type <span class="text-danger">*</span>
                </label>
                <b-form-select id="formType" v-model="selectedFormType" :options="formTypeOptions" required />
                <span class="text-danger" v-if="!$v.selectedFormType.required && $v.selectedFormType.$dirty">
                    Form Type is required!
                </span>
            </b-form-group>

            <!-- Form Name Input -->
            <b-form-group label-for="formName">
                <label for="formName" class="font-weight-bold">
                    Form Name <span class="text-danger">*</span>
                </label>
                <b-form-input id="formName" v-model="admissionform.name" placeholder="Enter Form Name" required />
                <span class="text-danger" v-if="!$v.admissionform.name.required && $v.admissionform.name.$dirty">
                    Form Name is required!
                </span>
            </b-form-group>

            <!-- Academic Year Select -->
            <b-form-group label-for="academicYear">
                <label for="academicYear" class="font-weight-bold">
                    Academic Year <span class="text-danger">*</span>
                </label>
                <b-form-select id="academicYear" disabled v-model="admissionform.academic_year"
                    :options="academicYears" required />
                <span class="text-danger"
                    v-if="!$v.admissionform.academic_year.required && $v.admissionform.academic_year.$dirty">
                    Academic Year is required!
                </span>
            </b-form-group>

            <!-- Academic Semester (read-only, auto-picked for semester registration forms) -->
            <b-form-group v-if="selectedFormType == 2" label-for="academicSemester">
                <label for="academicSemester" class="font-weight-bold">
                    Academic Semester
                </label>
                <b-form-input id="academicSemester" :value="currentSemesterLabel" disabled />
                <small class="text-muted">Auto-selected from your current semester setting.</small>
            </b-form-group>

            <!-- Programs (Usergroups) - only for semester registration forms -->
            <b-form-group v-if="selectedFormType == 2">
                <label class="font-weight-bold">
                    Programs <span class="text-danger">*</span>
                </label>
                <div v-if="loadingPrograms" class="text-muted small">Loading programs...</div>
                <div v-else-if="programOptions.length === 0" class="text-muted small">No programs found.</div>
                <div v-else class="border rounded p-2" style="max-height: 200px; overflow-y: auto;">
                    <b-form-checkbox
                        v-for="prog in programOptions"
                        :key="prog.id"
                        v-model="selectedProgramIds"
                        :value="prog.id"
                        class="mb-1"
                    >
                        {{ prog.name }} <small class="text-muted" v-if="prog.code">({{ prog.code }})</small>
                    </b-form-checkbox>
                </div>
            </b-form-group>

            <!-- Classes (Usersubgroups) - only for semester registration forms when programs are selected -->
            <b-form-group v-if="selectedFormType == 2 && selectedProgramIds.length > 0">
                <label class="font-weight-bold">
                    Classes
                </label>
                <div v-if="filteredClasses.length === 0" class="text-muted small">No classes found for the selected programs.</div>
                <div v-else class="border rounded p-2" style="max-height: 250px; overflow-y: auto;">
                    <b-form-checkbox
                        v-for="cls in filteredClasses"
                        :key="cls.id"
                        v-model="selectedClassIds"
                        :value="cls.id"
                        class="mb-1"
                    >
                        {{ cls.name }} <small class="text-muted" v-if="cls.code">({{ cls.code }})</small>
                        <small class="text-info" v-if="getProgramName(cls.usergroup_id)"> — {{ getProgramName(cls.usergroup_id) }}</small>
                    </b-form-checkbox>
                </div>
            </b-form-group>

            <!-- Start Date -->
            <b-form-group class="mr-2 mb-0">
                <label class="font-weight-bold">
                    Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker v-model="admissionform.start_date" placeholder="Select Start Date" required
                    menu-class="w-100" class="w-100"
                    :class="{ 'form-group-error': $v.admissionform.start_date.$error }" />
                <span class="text-danger small"
                    v-if="!$v.admissionform.start_date.required && $v.admissionform.start_date.$dirty">
                    Required!
                </span>
            </b-form-group>

            <!-- End Date -->
            <b-form-group class="mb-3">
                <label class="font-weight-bold">
                    End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker v-model="admissionform.end_date" placeholder="Select End Date" required
                    menu-class="w-100" class="w-100"
                    :class="{ 'form-group-error': $v.admissionform.end_date.$error }" />
                <span class="text-danger small"
                    v-if="!$v.admissionform.end_date.required && $v.admissionform.end_date.$dirty">
                    Required!
                </span>
            </b-form-group>
            <br>
            <!-- Bottom Buttons (Centered with Spacing) -->
            <div class="d-flex justify-content-center mt-4">
                <b-button variant="success" size="sm" @click="submitForm">
                    {{ admissionformId ? "Update" : "Submit" }}
                </b-button>
                <b-button variant="danger" size="sm" class="ml-2" @click="resetForm">
                    Cancel
                </b-button>
            </div>
        </b-form>

    </div>
</template>


<script>
import { required } from "vuelidate/lib/validators";

export default {
    props: ["selectedType", "formData"],
    async created() {
        console.log("admissionData", this.formData);
        if (this.formData) {
            this.initializing = true;
            this.admissionform = this.formData;
            this.admissionformId = this.formData.id;
            this.selectedFormType = this.formData.form_type || 1;
            if (this.formData.usergroups) {
                this.selectedProgramIds = this.formData.usergroups.map(g => g.id);
            }
            if (this.formData.usersubgroups) {
                this.selectedClassIds = this.formData.usersubgroups.map(s => s.id);
            }
            this.$nextTick(() => { this.initializing = false; });
        } else {
            const currentYear = this.$root.default_academic_year?.[0];
            if (currentYear && currentYear !== '0') {
                this.admissionform.academic_year = currentYear;
            }
        }
    },

    data() {
        return {
            admissionformId: null,
            initializing: false,
            selectedFormType: "",
            admissionform: {
                id: null,
                name: "",
                academic_year: "",
                start_date: "",
                end_date: "",
            },
            formTypeOptions: [
                { value: "", text: "-- Select Form Type --", disabled: true },
                { value: 1, text: "Enquiry/Admission Form" },
                { value: 2, text: "Semester Registration Form" },
            ],
            programOptions: [],
            loadingPrograms: false,
            selectedProgramIds: [],
            selectedClassIds: [],
        };
    },

    // Form validation rules
    validations: {
        selectedFormType: { required },
        admissionform: {
            name: { required },
            academic_year: { required },
            start_date: { required },
            end_date: { required },
        },
    },

    computed: {
        academicYears() {
            const startYear = 2019;
            const endYear = new Date().getFullYear() + 1;
            return Array.from({ length: endYear - startYear }, (_, i) => {
                const year1 = startYear + i;
                const year2 = (year1 + 1).toString().slice(-2);
                return `${year1}-${year2}`;
            });
        },
        currentSemesterLabel() {
            const sem = this.$root.default_academic_year?.[1];
            if (!sem || sem === '0') return 'Not selected — please set your semester in settings';
            return sem.charAt(0).toUpperCase() + sem.slice(1);
        },
        filteredClasses() {
            if (!this.selectedProgramIds.length) return [];
            return this.programOptions
                .filter(p => this.selectedProgramIds.includes(p.id))
                .flatMap(p => (p.usersubgroups || []).map(s => ({ ...s, usergroup_id: p.id })));
        },
    },

    watch: {
        admissionData: {
            immediate: true,
            handler(newData) {
                if (newData) {
                    this.admissionform = { ...newData };
                }
            }
        },
        selectedFormType(val) {
            if (val == 2 && this.programOptions.length === 0) {
                this.fetchProgramsWithClasses();
            }
            if (val != 2) {
                this.selectedProgramIds = [];
                this.selectedClassIds = [];
            }
        },
        selectedProgramIds() {
            if (!this.initializing) {
                this.selectedClassIds = [];
            }
        },
    },

    methods: {
        async fetchProgramsWithClasses() {
            this.loadingPrograms = true;
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups/withsubgroups`;
                const response = await this.$axios.get(url);
                this.programOptions = response.data;
            } catch (error) {
                console.error("Error fetching programs:", error);
            } finally {
                this.loadingPrograms = false;
            }
        },

        getProgramName(usergroupId) {
            const prog = this.programOptions.find(p => p.id === usergroupId);
            return prog ? prog.name : '';
        },

        async submitForm() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            const url = this.admissionform.id
                ? `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/${this.admissionform.id}`
                : `${this.$store.getters.getAPIKey.mainAPI}/admissionforms`;

            const method = this.admissionform.id ? "put" : "post";

            const payload = { ...this.admissionform };
            if (!this.admissionform.id) {
                payload.form_type = this.selectedFormType;
            }
            if (this.selectedFormType == 2) {
                payload.usergroup_ids = this.selectedProgramIds;
                payload.usersubgroup_ids = this.selectedClassIds;
            }

            await this.$axios[method](url, payload)
                .then(() => {
                    this.$toast.success("Form Submitted successfully", {
                        position: "top",
                        duration: 3000,
                    });
                })
                .catch((error) => {
                    let errortxt = error.response.data.message ?? 'Failed to Submit, please try agin';
                    this.$toast.error(errortxt, {
                        position: "top",
                        duration: 7000,
                    });
                });

            this.resetForm();
        },

        resetForm() {
            this.$v.$reset();
            this.selectedFormType = "";
            this.selectedProgramIds = [];
            this.selectedClassIds = [];
            this.admissionform = { name: "", academic_year: "", start_date: "", end_date: "" };
            this.$emit("closeForm");
            this.$emit("reloadAdmissionFormList");
            this.formData = {};
        },
    },
};
</script>
